import BaseStudentModel from "~/models/eschoolplus/registration/Student";
import dayjs from "dayjs";
import CourseRequest from "./CourseRequest";
import EspCourseRequest from "./EspCourseRequest";
import EspAltCourseRequest from "./EspAltCourseRequest";
import Message from "./Message";
import Note from "./Note";
import Status from "./Status";
import Recommendation from "./Recommendation";
import ApRecommendation from "./ApRecommendation";
import RequestSync from "./RequestSync";
import TestHistory from "./TestHistory";

export default class Student extends BaseStudentModel {
  // Define a base url for a REST API
  baseURL() {
    return useRuntimeConfig().public.apiBase + "/api/v2/courseplanner";
  }

  // Set the resource route of the model
  resource() {
    return "students";
  }

  relations() {
    return {
      current_planner_status: Status,
      // course_requests: CourseRequest,
      current_course_requests: CourseRequest,
      current_staff_course_requests: CourseRequest,
      current_student_course_requests: CourseRequest,
      esp_current_course_requests: EspCourseRequest,
      esp_current_placements: EspCourseRequest,
      esp_current_alternates: EspAltCourseRequest,
      messages: Message,
      scheduling_notes: Note,
      teacher_recommendations: Recommendation,
      current_teacher_recommendations: Recommendation,
      ap_recommendations: ApRecommendation,
      current_ap_recommendations: ApRecommendation,
    };
  }

  // Lazy "Relationships"
  request_sync() {
    return this.hasMany(RequestSync);
  }
  status_sync() {
    return this.hasMany(Status);
  }
  test_history_sync() {
    return this.hasMany(TestHistory);
  }

  // Computed Attributes
  get student_requests() {
    return {
      all: this.current_course_requests,
      student: this.current_student_course_requests,
      staff: this.current_staff_course_requests,
      placement: this.esp_current_placements,
      eschool: this.esp_current_course_requests,
      eschool_alt: this.esp_current_alternates,
    };
  }

  get staff_requests_credits() {
    if (!this.current_staff_course_requests) {
      return 0.0;
    }

    let credits = 0.0;

    // Calculate selected choices
    for (let i in this.current_staff_course_requests) {
      // Only count regular staff requests.
      if (
        this.current_staff_course_requests[i].requester_type != "staff" ||
        this.current_staff_course_requests[i].request_type != "regular"
      ) {
        continue;
      }

      if (this.current_staff_course_requests[i].course_info) {
        credits += this.current_staff_course_requests[i].course_info.CREDIT;
      } else {
        // TODO: Lookup Credit
      }
    }

    return credits;
  }

  get reported_siblings() {
    return this.messages.filter((i) => i.message_type == "reported_siblings");
  }

  get summer_school_interests() {
    return this.messages.filter(
      (i) => i.message_type == "summer_school_interest"
    );
  }

  get summer_courses() {
    return this.messages.filter((i) => i.message_type == "summer_course");
  }

  // Helper Functions
  isCPRequestSyncedToESchool(request) {
    // Check if request has been saved to the database
    if (!request || !request.id) {
      return false;
    }

    switch (request.request_type) {
      case "regular":
        // Check if any reqular requests have been saved to eSchool
        if (!this.esp_current_course_requests.length) {
          return false;
        }

        const cp_finalized =
          request.is_finalized === true || request.is_finalized == "1";

        const matching_req = this.esp_current_course_requests.find(function (
          esp_request
        ) {
          const esp_finalized = esp_request.IS_LOCKED == "Y";

          // Compare Marking Periods
          const cp_marking_periods = request.course_marking_period
            ? request.course_marking_period.split(",")
            : [];
          const esp_marking_periods = [];
          let mp_match = false;

          // Organizes eSP request MPs
          if (
            esp_request.marking_periods &&
            esp_request.marking_periods.length
          ) {
            for (let i = 0; i < esp_request.marking_periods.length; i++) {
              esp_marking_periods.push(
                esp_request.marking_periods[i].MARKING_PERIOD.trim()
              );
            }
          }

          // If there are at least the same amount of MPs, compare each value.
          if (cp_marking_periods.length == esp_marking_periods.length) {
            mp_match = true;
            for (let i = 0; i < cp_marking_periods.length; i++) {
              if (!esp_marking_periods.includes(cp_marking_periods[i])) {
                mp_match = false;
                break;
              }
            }
          }

          // See if all the details match for the regular request
          return (
            esp_request.REQUEST_TYPE == "R" && // Regular eSchool Request
            esp_request.SCHOOL_YEAR == request.school_year && // Check matching school year
            esp_request.COURSE.trim() == request.course.trim() && // Check matching course number
            esp_finalized == cp_finalized && // Check is finalized
            mp_match // Check matching marking periods
          );
        });

        // Did we find a matching course request?
        if (matching_req === undefined) return false;

        // We found one! Now lets compare alternate course info.
        var matching_req_alt = this.esp_current_course_requests.find(function (
          esp_request
        ) {
          var esp_finalized = esp_request.IS_LOCKED == "Y";
          var esp_alt_to_course = esp_request.ALT_TO_REQUEST
            ? esp_request.ALT_TO_REQUEST.trim()
            : null;
          var esp_alt_course = esp_request.COURSE.trim();
          var cp_alt_course_match = request.alt_course
            ? esp_alt_course == request.alt_course.trim()
            : true;

          return (
            esp_request.REQUEST_TYPE == "C" && // Regular eSchool Request
            esp_request.SCHOOL_YEAR == request.school_year && // Check matching school year
            cp_alt_course_match && // Check matching alt course number
            esp_alt_to_course == request.course.trim() && // Check matching course number
            esp_finalized == cp_finalized // Check is finalized
          );
        });

        if (request.alt_course && matching_req_alt === undefined) {
          // We have an alt course but none was found an eSchool.
          return false;
        } else if (!request.alt_course && matching_req_alt !== undefined) {
          // We don't have an alt course, but one was found in eSchool.
          return false;
        }

        return true;

      case "alternate":
        // Check if any alternate requests have been saved to eSchool
        if (!this.esp_current_alternates.length) {
          return false;
        }

        var matching_alt = this.esp_current_alternates.find(function (
          esp_request
        ) {
          return (
            esp_request.school_year == request.school_year && // Check matching school year
            esp_request.course.trim() == request.course.trim() && // Check matching course number
            esp_request.alternate_sequence == request.alt_course_priority // Check matching priority
          );
        });

        // Did we find a matching course request?
        if (matching_alt === undefined) return false;

        return true;
    }
  }

  isCPStaffRequest(courseId, request_type = null) {
    if (!this.student_requests.staff.length || !courseId) {
      return false;
    }

    return (
      this.student_requests.staff.findIndex(function (request) {
        switch (request_type) {
          case "regular":
            return (
              request.request_type == "regular" &&
              request.course.trim() == courseId.trim()
            );
          case "course_alternate":
            return (
              request.request_type == "regular" &&
              request.alt_course &&
              request.alt_course.trim() == courseId.trim()
            );
          case "alternate":
            return (
              request.request_type == "alternate" &&
              request.course.trim() == courseId.trim()
            );
          default:
            return request.course.trim() == courseId.trim();
        }
      }) !== -1
    );
  }

  getGradReqCompletedTotal(code) {
    if (!this.academic || !this.academic.grad_reqs_completed) {
      return 0.0;
    }
    const reqs_completed = this.academic.grad_reqs_completed || [];
    let total = 0.0;

    for (let i in reqs_completed) {
      if (
        (!code || reqs_completed[i].REQUIRE_CODE.startsWith(code)) &&
        reqs_completed[i].SUBJ_AREA_CREDIT
      ) {
        total += reqs_completed[i].SUBJ_AREA_CREDIT;
      }
    }
    return total;
  }

  getGradReqRequiredTotal(code) {
    if (!this.academic || !this.academic.grad_reqs) {
      return 0.0;
    }
    const reqs_required = this.academic.grad_reqs || [];
    let total = 0.0;

    for (let i in reqs_required) {
      if (
        (!code || reqs_required[i].REQUIRE_CODE.startsWith(code)) &&
        reqs_required[i].CREDIT
      ) {
        total += reqs_required[i].CREDIT;
      }
    }
    return total;
  }

  get display_grad_req_calculated_at() {
    if (
      !this.academic ||
      !this.academic.grad_reqs_completed ||
      this.academic.grad_reqs_completed.length == 0
    ) {
      return "N/A";
    }
    return dayjs(this.academic.grad_reqs_completed[0].CHANGE_DATE_TIME).format(
      "MM/DD/YYYY"
    );
  }

  getRecommendationsForDept(dept) {
    dept = dept ? dept.trim() : dept;
    return (
      this.current_teacher_recommendations.filter((rec) => {
        return (
          dept ==
          (rec.course_department_id
            ? rec.course_department_id.trim()
            : rec.course_department_id)
        );
      }) || []
    );
  }

  getCPCourseRequestByDept(dept, requesterType = "all", requestType) {
    if (!this.student_requests[requesterType]) {
      return [];
    }
    dept = dept ? dept.trim() : dept;
    return (
      this.student_requests[requesterType].filter((request) => {
        var request_dept = request.course_dept
          ? request.course_dept.trim()
          : request.course_dept;
        return (
          request_dept == dept &&
          (!requestType || request.request_type == requestType)
        );
      }) || []
    );
  }

  getEschoolRequestByDept(dept, requestType = "eschool") {
    if (!this.student_requests[requestType]) {
      return [];
    }
    dept = dept ? dept.trim() : dept;
    return (
      this.student_requests[requestType].filter((request) => {
        if (!request || !request.course_info) {
          return false;
        }
        var request_dept = request.course_info.DEPARTMENT
          ? request.course_info.DEPARTMENT.trim()
          : request.course_info.DEPARTMENT;
        return request_dept == dept || request.COURSE.substring(0, 3) == dept;
      }) || []
    );
  }

  getStudentCourseRequestsForDept(dept, requestType = false) {
    return this.getCPCourseRequestByDept(dept, "student", requestType);
  }

  /**
   *  Handle Changes to Related Data
   **/
  updateRelatedData(model, myKey, matchFn) {
    //console.log("updateRelatedData", model, myKey, matchFn);
    if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
      // Don't care about save.
      return;
    }

    const dataIndex = this[myKey].findIndex(matchFn);
    if (dataIndex == -1) {
      this[myKey].push(model);
    } else {
      this[myKey].splice(dataIndex, 1, model);
    }
  }

  removeRelatedData(model, myKey, matchFn) {
    //console.log("removeRelatedData", model, myKey, matchFn);
    if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
      // Don't care about save.
      return;
    }

    const dataIndex = this[myKey].findIndex(matchFn);
    if (dataIndex != -1) {
      this[myKey].splice(dataIndex, 1);
    }
  }

  // Updated Data
  savedNote(note) {
    const model = new Note(note).for(this);

    this.updateRelatedData(
      model,
      "scheduling_notes",
      (i) => i.record_id == model.record_id
    );
  }

  deletedNote(note) {
    this.removeRelatedData(
      note,
      "scheduling_notes",
      (i) => i.record_id == note.record_id
    );
  }

  savedMessage(message) {
    const model = new Message(message).for(this);

    this.updateRelatedData(model, "messages", (i) => i.id == model.id);
  }

  deletedMessage(message) {
    this.removeRelatedData(message, "messages", (i) => i.id == message.id);
  }

  savedCourseRequest(courseRequest) {
    const model = new CourseRequest(courseRequest).for(this);

    this.updateRelatedData(
      model,
      "current_course_requests",
      (i) => i.id == model.id
    );

    switch (courseRequest.requester_type) {
      case "student":
        this.updateRelatedData(
          model,
          "current_student_course_requests",
          (i) => i.id == model.id
        );
        break;

      case "staff":
        this.updateRelatedData(
          model,
          "current_staff_course_requests",
          (i) => i.id == model.id
        );
        break;
    }
  }

  deletedCourseRequest(courseRequest) {
    this.removeRelatedData(
      courseRequest,
      "current_course_requests",
      (i) => i.id == courseRequest.id
    );
    this.removeRelatedData(
      courseRequest,
      "current_student_course_requests",
      (i) => i.id == courseRequest.id
    );
    this.removeRelatedData(
      courseRequest,
      "current_staff_course_requests",
      (i) => i.id == courseRequest.id
    );
  }
}
